import { v4 as uuidv4 } from 'uuid';
import { RideSchema, FleetSettings, Vehicle } from '../../types/simulation';
import { DEFAULT_SIMULATION_DATE } from '../../config/constants';
import { combineDateWithTime } from '../../utils/dateTimeUtils';

// Helper to convert snake_case to camelCase
function toCamelCase(str: string) {
  return str.replace(/_([a-z])/g, g => g[1].toUpperCase());
}

// Recursively convert all keys in an object to camelCase
function keysToCamelCase(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(keysToCamelCase);
  } else if (obj && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [toCamelCase(k), keysToCamelCase(v)])
    );
  }
  return obj;
}

/**
 * Converts HH:mm time string to ISO datetime string using the default simulation date
 */
function convertTimeToISO(timeStr: string): string {
  if (!timeStr) return '';
  
  // If already in ISO format, return as is
  if (timeStr.includes('T') || timeStr.includes('-')) {
    return timeStr;
  }
  
  // Use our timezone-aware function to convert HH:mm to UTC ISO format
  return combineDateWithTime(DEFAULT_SIMULATION_DATE, timeStr);
}

export function mapSimulationToProblemConfig(simulation: any) {
  const problemId = uuidv4();

  // Merge settings and problem_config, convert keys to camelCase
  // IMPORTANT: problem_config should take precedence over timeWindowSettings
  const configuration = {
    scenarioName: simulation.scenarioName,
    timeModelType: simulation.timeWindowSettings?.time_model_type,
    ...keysToCamelCase(simulation.timeWindowSettings?.settings || {}),
    // Override with problem_config values to preserve TTM/VTTM settings
    ...keysToCamelCase(simulation.problem_config?.configuration || {}),
    // Add/merge any other fields as required by backend schema
  };

  return {
    id: problemId,
    configuration,
  };
}

// Generated ride interface from generate-rides component
interface GeneratedRide {
  id: string;
  from_latitude: number;
  from_longitude: number;
  to_latitude: number;
  to_longitude: number;
  from_address?: string;
  to_address?: string;
  request_time: string;  // ISO format
  passengers: {
    number_of_passengers: number;
    type: 'standard' | 'wheelchair';
  }[];
  batch_id?: string;
}

type RideLike = GeneratedRide &
  Partial<RideSchema> & {
    id?: string;
    rideId?: string;
    externalReferenceId?: string;
    requestType?: string;
  };

/**
 * Picks a stable trip / external reference id from either generator rides (`id`) or layer rides
 * (`external_reference_id` / `ride_id`). Shape UUIDs must not be used as the trip ref when a
 * human-readable id is present on the ride object.
 */
export function pickTripExternalReferenceId(ride: RideLike): string {
  const candidates: unknown[] = [
    ride.external_reference_id,
    ride.externalReferenceId,
    ride.ride_id,
    ride.rideId,
    ride.id,
  ];
  for (const c of candidates) {
    if (c == null) continue;
    if (typeof c === 'string') {
      const t = c.trim();
      if (t.length > 0) return t;
    }
    if (typeof c === 'number' && Number.isFinite(c)) return String(c);
  }
  return uuidv4();
}

/**
 * Maps generated rides from the generate-rides drawer (or layer-derived {@link RideSchema} rows)
 * to API {@link RideSchema}. Layer rows use `ride_id` / `external_reference_id`; generator rows use `id`.
 */
export function mapGeneratedRidesToApi(rides: RideLike[]): RideSchema[] {
  return rides.map((ride) => {
    const ext = pickTripExternalReferenceId(ride);
    const merged: RideSchema & { id?: string } = {
      ...ride,
      external_reference_id: ext,
      ride_id: ride.ride_id ?? ext,
      request_type: ride.request_type ?? ride.requestType ?? 'departure_fixed',
      passengers: ride.passengers.map((p) => ({
        number_of_passengers: p.number_of_passengers,
        type: p.type,
      })),
      batch_id: ride.batch_id || undefined,
    };
    delete merged.id;
    return merged;
  });
} 
